import { useCallback, useRef, useEffect, useState } from 'react';
import { useAPI } from '../clients/quipli-service/quipli-service-client';

interface UseDebouncedConfigUpdatesOptions {
  /**
   * Delay in milliseconds before triggering the update
   * @default 500
   */
  delay?: number;

  /**
   * Callback fired when update succeeds
   */
  onSuccess?: (variable: string, value: string) => void;

  /**
   * Callback fired when update fails
   */
  onError?: (error: any, variable: string, value: string) => void;

  /**
   * Enable loading state tracking
   * @default false
   */
  trackLoading?: boolean;
}

interface UseDebouncedConfigUpdatesReturn {
  /**
   * Update a single configuration value with debounce
   */
  updateConfig: (variable: string, value: string) => void;

  /**
   * Update multiple configuration values with debounce
   * All updates are batched and executed together after the delay
   */
  updateConfigs: (configs: Array<{ variable: string; value: string }>) => void;

  /**
   * Cancel any pending updates
   */
  cancelPendingUpdates: () => void;

  /**
   * Whether an update is currently in progress
   */
  isUpdating: boolean;

  /**
   * Whether there are pending updates waiting to be executed
   */
  hasPendingUpdates: boolean;
}

/**
 * Hook to debounce configuration updates for real-time auto-save functionality
 *
 * @example
 * ```tsx
 * const { updateConfig, isUpdating } = useDebouncedConfigUpdates({
 *   delay: 1000,
 *   onSuccess: (variable) => console.log(`${variable} saved!`),
 * });
 *
 * // In your component
 * <input
 *   value={value}
 *   onChange={(e) => {
 *     setValue(e.target.value);
 *     updateConfig('CONF_MY_CONFIG', e.target.value);
 *   }}
 * />
 * ```
 */
export const useDebouncedConfigUpdates = (
  options: UseDebouncedConfigUpdatesOptions = {},
): UseDebouncedConfigUpdatesReturn => {
  const { delay = 500, onSuccess, onError, trackLoading = false } = options;
  const { updateConfiguration } = useAPI();

  const timeoutRef = useRef<NodeJS.Timeout>();
  const pendingUpdatesRef = useRef<Map<string, string>>(new Map());

  // Track loading state
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Track pending updates
  const [hasPendingUpdates, setHasPendingUpdates] = useState<boolean>(false);

  // Execute pending updates
  const executePendingUpdates = useCallback(async () => {
    if (pendingUpdatesRef.current.size === 0) return;

    if (trackLoading) {
      setIsUpdating(true);
    }
    setHasPendingUpdates(false);

    const updates = Array.from(pendingUpdatesRef.current.entries());
    pendingUpdatesRef.current.clear();

    try {
      // Execute all updates in parallel
      await Promise.all(
        updates.map(([variable, value]) =>
          updateConfiguration(variable, value)
            .then(() => {
              onSuccess?.(variable, value);
            })
            .catch((error) => {
              console.error(`Failed to update configuration ${variable}:`, error);
              onError?.(error, variable, value);
              throw error;
            }),
        ),
      );
    } finally {
      if (trackLoading) {
        setIsUpdating(false);
      }
    }
  }, [updateConfiguration, onSuccess, onError, trackLoading]);

  // Schedule update
  const scheduleUpdate = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setHasPendingUpdates(true);

    // Schedule new update
    timeoutRef.current = setTimeout(() => {
      executePendingUpdates();
    }, delay);
  }, [delay, executePendingUpdates]);

  // Update a single configuration value with debounce
  const updateConfig = useCallback(
    (variable: string, value: string) => {
      pendingUpdatesRef.current.set(variable, value);
      scheduleUpdate();
    },
    [scheduleUpdate],
  );

  // Update multiple configuration values with debounce
  const updateConfigs = useCallback(
    (configs: Array<{ variable: string; value: string }>) => {
      configs.forEach(({ variable, value }) => {
        pendingUpdatesRef.current.set(variable, value);
      });
      scheduleUpdate();
    },
    [scheduleUpdate],
  );

  // Cancel all pending updates
  const cancelPendingUpdates = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    pendingUpdatesRef.current.clear();
    setHasPendingUpdates(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    updateConfig,
    updateConfigs,
    cancelPendingUpdates,
    isUpdating,
    hasPendingUpdates,
  };
};
